import React, { useState, useRef } from 'react';
import { Heart, MessageCircle, Share2, Bookmark, MoreHorizontal, Lock, Gift, MapPin, ChevronLeft, Bell, BellOff, CheckCircle, Flame, ArrowLeft, ShoppingBag, Image as ImageIcon, Mic, Flag, Target, Music, Award, DollarSign, Send, ChevronRight } from 'lucide-react';
import { AngelFeedFilters } from './AngelFeedFilters';
interface HarperAngelFeedProps {
  onBack: () => void;
}
export const HarperAngelFeed: React.FC<HarperAngelFeedProps> = ({
  onBack
}) => {
  const [activeTab, setActiveTab] = useState('all');
  const [isFollowing, setIsFollowing] = useState(false);
  const [likedPosts, setLikedPosts] = useState<Record<string, boolean>>({});
  const [showNotificationOptions, setShowNotificationOptions] = useState(false);
  const [notificationEnabled, setNotificationEnabled] = useState(false);
  const [showPollResults, setShowPollResults] = useState(false);
  const [pollVote, setPollVote] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockPostId, setUnlockPostId] = useState<string | null>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const tabs = [{
    id: 'all',
    label: 'All',
    icon: <Flame size={14} data-id="element-8229" />
  }, {
    id: 'media',
    label: 'Media',
    icon: <ImageIcon size={14} data-id="element-8230" />
  }, {
    id: 'voice',
    label: 'Voice',
    icon: <Mic size={14} data-id="element-8231" />
  }, {
    id: 'missions',
    label: 'Missions',
    icon: <Target size={14} data-id="element-8232" />
  }, {
    id: 'moans',
    label: 'Moans',
    icon: <Music size={14} data-id="element-8233" />
  }, {
    id: 'polls',
    label: 'Polls',
    icon: <Flag size={14} data-id="element-8234" />
  }, {
    id: 'upsells',
    label: 'Upsells',
    icon: <Lock size={14} data-id="element-8235" />
  }, {
    id: 'rewards',
    label: 'Rewards',
    icon: <Award size={14} data-id="element-8236" />
  }, {
    id: 'shop',
    label: 'Shop',
    icon: <ShoppingBag size={14} data-id="element-8237" />
  }];
  const scrollTabs = (direction: 'left' | 'right') => {
    if (tabsRef.current) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      tabsRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  const toggleFollow = () => {
    setIsFollowing(!isFollowing);
  };
  const toggleNotification = () => {
    setNotificationEnabled(!notificationEnabled);
    setShowNotificationOptions(false);
  };
  const toggleLike = (postId: string) => {
    setLikedPosts(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };
  const handlePollVote = (option: string) => {
    setPollVote(option);
    setShowPollResults(true);
  };
  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would submit the comment
    setCommentText('');
  };
  const handleUnlock = (postId: string) => {
    setUnlockPostId(postId);
    setShowUnlockModal(true);
  };
  const confirmUnlock = () => {
    // Here you would handle the actual token transaction
    setShowUnlockModal(false);
  };
  const posts = [{
    id: 'post1',
    type: 'premium_content',
    content: "Just finished my yoga session... want to see how flexible I can be? 💙 My ice blue eyes get even more intense when I'm stretching...",
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8g53dZ7J3kRR9dJNnGuDbc/h2.png',
    likes: 3452,
    comments: 287,
    time: '1h ago',
    locked: true,
    price: 8,
    location: 'Yoga Studio',
    hashtags: ['#yoga', '#flexible', '#blueeyes', '#exclusive']
  }, {
    id: 'post2',
    type: 'poll',
    content: "What should I wear for tonight's private stream? I'll be showing off some new moves... 💙",
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8g53dZ7J3kRR9dJNnGuDbc/h2.png',
    likes: 1789,
    comments: 312,
    time: '4h ago',
    location: 'Getting Ready',
    poll: {
      options: [{
        text: 'Blue lingerie 💙',
        percentage: 65
      }, {
        text: 'Yoga outfit 🧘‍♀️',
        percentage: 35
      }],
      endsIn: '2 hours'
    },
    hashtags: ['#outfitpoll', '#privatestream', '#helpmedecide', '#icequeen']
  }, {
    id: 'post3',
    type: 'voice_note',
    content: 'Listen to my breathing during my intense workout... some sounds are just too good to miss 🎧',
    likes: 2231,
    comments: 172,
    time: '1d ago',
    location: 'Home Gym',
    audioWaveform: [0.2, 0.5, 0.3, 0.8, 0.4, 0.6, 0.3, 0.5, 0.7, 0.8, 0.5, 0.3, 0.6, 0.4, 0.2],
    duration: '0:47',
    hashtags: ['#voice', '#workout', '#asmr', '#heavybreathing']
  }];
  // Filter posts based on active tab
  const filteredPosts = posts.filter(post => {
    if (activeTab === 'all') return true;
    if (activeTab === 'media' && (post.type === 'image' || post.type === 'premium_content')) return true;
    if (activeTab === 'voice' && post.type === 'voice_note') return true;
    if (activeTab === 'polls' && post.type === 'poll') return true;
    if (activeTab === 'upsells' && post.type === 'premium_content') return true;
    return false;
  });
  return <section className="w-full max-w-2xl mx-auto space-y-4 bg-white dark:bg-gray-900 text-of-text-primary dark:text-white" data-id="element-8238">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-900/90 backdrop-blur-sm shadow-sm p-3 flex items-center justify-between border-b border-of-gray dark:border-gray-800" data-id="element-8239">
        <div className="flex items-center gap-4" data-id="element-8240">
          <button className="p-2 rounded-full hover:bg-of-gray dark:hover:bg-gray-800" onClick={() => {
          // Navigate back to AllAngelsGrid
          const navigateEvent = new CustomEvent('navigate', {
            detail: {
              section: 'all_angels'
            }
          });
          document.dispatchEvent(navigateEvent);
          // Still call the original onBack for any cleanup
          onBack();
        }} data-id="element-8241">
            <ArrowLeft size={20} className="text-of-text-primary dark:text-gray-300" data-id="element-8242" />
          </button>
          <div data-id="element-8243">
            <h1 className="font-bold text-lg dark:text-white" data-id="element-8244">Harper</h1>
            <p className="text-xs text-of-text-secondary dark:text-gray-400" data-id="element-8245">
              2.8K Followers
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-8246">
          <div className="relative" data-id="element-8247">
            <button className="p-2 bg-of-gray dark:bg-gray-800 rounded-full hover:bg-of-gray-dark dark:hover:bg-gray-700" onClick={() => setShowNotificationOptions(!showNotificationOptions)} data-id="element-8248">
              {notificationEnabled ? <Bell size={20} className="text-of-blue dark:text-blue-400" data-id="element-8249" /> : <BellOff size={20} className="text-of-text-secondary dark:text-gray-400" data-id="element-8250" />}
            </button>
            {showNotificationOptions && <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-of-gray dark:border-gray-700 z-10" data-id="element-8251">
                <button className="w-full px-4 py-2 text-left hover:bg-of-gray-light dark:hover:bg-gray-700 rounded-t-lg flex items-center gap-2" onClick={toggleNotification} data-id="element-8252">
                  {notificationEnabled ? <>
                      <BellOff size={16} className="text-of-text-secondary dark:text-gray-400" data-id="element-8253" />
                      <span className="dark:text-gray-300" data-id="element-8254">
                        Disable notifications
                      </span>
                    </> : <>
                      <Bell size={16} className="text-of-blue dark:text-blue-400" data-id="element-8255" />
                      <span className="dark:text-gray-300" data-id="element-8256">
                        Enable notifications
                      </span>
                    </>}
                </button>
              </div>}
          </div>
          <button className={`px-4 py-1.5 rounded-full font-bold ${isFollowing ? 'bg-of-gray dark:bg-gray-800 text-of-text-primary dark:text-white hover:bg-red-100 dark:hover:bg-red-900 hover:text-red-600 dark:hover:text-red-400' : 'bg-of-blue dark:bg-blue-600 text-white hover:bg-of-blue-dark dark:hover:bg-blue-700'}`} onClick={toggleFollow} data-id="element-8257">
            {isFollowing ? 'Following' : 'Follow'}
          </button>
        </div>
      </div>

      {/* Profile Header */}
      <div className="relative" data-id="element-8258">
        <div className="h-32 w-full bg-gradient-to-r from-blue-600 to-purple-600 rounded-t-xl" data-id="element-8259"></div>
        <div className="absolute -bottom-16 left-4 w-32 h-32 rounded-full border-4 border-white dark:border-gray-900 overflow-hidden" data-id="element-8260">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/8g53dZ7J3kRR9dJNnGuDbc/h2.png" alt="Harper" className="w-full h-full object-cover" data-id="element-8261" />
        </div>
      </div>

      {/* Profile Info */}
      <div className="mt-20 px-4 ml-36" data-id="element-8262">
        <div className="flex items-center gap-1" data-id="element-8263">
          <h2 className="text-xl font-bold dark:text-white" data-id="element-8264">Harper</h2>
          <CheckCircle size={16} className="text-blue-400" fill="currentColor" data-id="element-8265" />
          <span className="text-sm font-normal text-blue-400 ml-2" data-id="element-8266">💙❄️</span>
        </div>
        <p className="text-of-text-secondary dark:text-gray-400" data-id="element-8267">@IceQueen</p>
        <p className="mt-2 dark:text-gray-300" data-id="element-8268">
          Ice blue eyes, fire-hot touch 💙🔥 Yoga enthusiast & fitness model. My
          flexibility will amaze you...
        </p>
        <div className="flex gap-4 mt-3 text-sm" data-id="element-8269">
          <div className="flex items-center gap-1" data-id="element-8270">
            <span className="font-bold dark:text-white" data-id="element-8271">64</span>
            <span className="text-of-text-secondary dark:text-gray-400" data-id="element-8272">
              Following
            </span>
          </div>
          <div className="flex items-center gap-1" data-id="element-8273">
            <span className="font-bold dark:text-white" data-id="element-8274">2.8K</span>
            <span className="text-of-text-secondary dark:text-gray-400" data-id="element-8275">
              Followers
            </span>
          </div>
          <div className="flex items-center gap-1" data-id="element-8276">
            <span className="font-bold dark:text-white" data-id="element-8277">315</span>
            <span className="text-of-text-secondary dark:text-gray-400" data-id="element-8278">
              Intimacy XP
            </span>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="mt-4 relative border-b border-of-gray dark:border-gray-800" data-id="element-8279">
        <button className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 rounded-full text-of-text-secondary dark:text-gray-400 hover:text-of-text-primary dark:hover:text-gray-300" onClick={() => scrollTabs('left')} data-id="element-8280">
          <ChevronLeft size={18} data-id="element-8281" />
        </button>
        <div ref={tabsRef} className="flex overflow-x-auto scrollbar-hide px-8 py-1" data-id="element-8282">
          {tabs.map(tab => <button key={tab.id} className={`px-4 py-2 flex items-center gap-1.5 whitespace-nowrap rounded-full mx-1 ${activeTab === tab.id ? 'bg-of-blue dark:bg-blue-600 text-white' : 'bg-of-gray dark:bg-gray-800 text-of-text-primary dark:text-gray-300 hover:bg-of-gray-dark dark:hover:bg-gray-700'}`} onClick={() => setActiveTab(tab.id)} data-id="element-8283">
              {tab.icon}
              <span data-id="element-8284">{tab.label}</span>
            </button>)}
        </div>
        <button className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 rounded-full text-of-text-secondary dark:text-gray-400 hover:text-of-text-primary dark:hover:text-gray-300" onClick={() => scrollTabs('right')} data-id="element-8285">
          <ChevronRight size={18} data-id="element-8286" />
        </button>
      </div>

      {/* Posts Feed */}
      <div className="space-y-4" data-id="element-8287">
        {filteredPosts.length > 0 ? filteredPosts.map(post => <div key={post.id} className="bg-white dark:bg-gray-800 rounded-xl border border-of-gray dark:border-gray-700 overflow-hidden animate-in fade-in duration-300" data-id="element-8288">
              {/* Post Header */}
              <div className="p-3 flex items-center gap-2 border-b border-of-gray dark:border-gray-700" data-id="element-8289">
                <div className="w-10 h-10 rounded-full overflow-hidden" data-id="element-8290">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/8g53dZ7J3kRR9dJNnGuDbc/h2.png" alt="Harper" className="w-full h-full object-cover" data-id="element-8291" />
                </div>
                <div data-id="element-8292">
                  <div className="flex items-center gap-1" data-id="element-8293">
                    <h3 className="font-medium dark:text-white" data-id="element-8294">Harper</h3>
                    <CheckCircle size={14} className="text-blue-400" fill="currentColor" data-id="element-8295" />
                    <span className="text-xs text-of-text-secondary dark:text-gray-400" data-id="element-8296">
                      • {post.time}
                    </span>
                  </div>
                  {post.location && <div className="flex items-center gap-1 text-xs text-of-text-secondary dark:text-gray-400" data-id="element-8297">
                      <MapPin size={10} className="ml-1" data-id="element-8298" />
                      <span data-id="element-8299">{post.location}</span>
                    </div>}
                </div>
                <button className="ml-auto" data-id="element-8300">
                  <MoreHorizontal size={16} className="text-of-text-secondary dark:text-gray-400" data-id="element-8301" />
                </button>
              </div>

              {/* Post Content */}
              <div className="px-3 py-2" data-id="element-8302">
                <p className="text-sm dark:text-gray-300" data-id="element-8303">{post.content}</p>
                {/* Hashtags */}
                {post.hashtags && <div className="text-xs text-blue-500 dark:text-blue-400 mt-1" data-id="element-8304">
                    {post.hashtags.join(' ')}
                  </div>}
              </div>

              {/* Post Media */}
              {(post.type === 'image' || post.type === 'poll' && post.image) && post.image && <div className="aspect-square bg-of-gray-light dark:bg-gray-700" data-id="element-8305">
                    <img src={post.image} alt="Post" className="w-full h-full object-cover" data-id="element-8306" />
                  </div>}

              {/* Premium Content */}
              {post.type === 'premium_content' && post.image && <div className="relative aspect-square bg-of-gray-light dark:bg-gray-700" data-id="element-8307">
                  <img src={post.image} alt="Locked Content" className="w-full h-full object-cover blur-sm" data-id="element-8308" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 backdrop-blur-sm" data-id="element-8309">
                    <Lock size={32} className="text-blue-400" data-id="element-8310" />
                    <p className="text-center text-white font-medium" data-id="element-8311">
                      Unlock Exclusive Photo
                    </p>
                    <button className="mt-3 px-4 py-2 bg-of-blue dark:bg-blue-600 rounded-full text-white font-medium flex items-center gap-2" onClick={() => handleUnlock(post.id)} data-id="element-8312">
                      <span data-id="element-8313">Unlock for</span>
                      <span className="text-blue-200" data-id="element-8314">{post.price} 💎</span>
                    </button>
                  </div>
                </div>}

              {/* Poll */}
              {post.type === 'poll' && post.poll && <div className="p-3" data-id="element-8315">
                  <div className="space-y-2" data-id="element-8316">
                    {post.poll.options.map((option, i) => <button key={i} className={`w-full p-3 bg-white dark:bg-gray-700 rounded-lg border ${pollVote === option.text ? 'border-blue-500 dark:border-blue-400' : 'border-gray-200 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400'} transition flex justify-between items-center`} onClick={() => handlePollVote(option.text)} data-id="element-8317">
                        <span className="text-gray-800 dark:text-gray-200" data-id="element-8318">
                          {option.text}
                        </span>
                        {(showPollResults || pollVote) && <span className="text-xs bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded-full text-gray-700 dark:text-gray-300" data-id="element-8319">
                            {option.percentage}%
                          </span>}
                      </button>)}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2" data-id="element-8320">
                      Voting ends in {post.poll.endsIn} • Results will shape
                      tonight's stream
                    </p>
                  </div>
                </div>}

              {/* Voice Note */}
              {post.type === 'voice_note' && post.audioWaveform && <div className="p-3" data-id="element-8321">
                  <div className="p-3 bg-of-gray-light dark:bg-gray-700 rounded-lg border border-of-gray dark:border-gray-600" data-id="element-8322">
                    <div className="flex items-center gap-3" data-id="element-8323">
                      <button className="w-10 h-10 flex items-center justify-center rounded-full bg-of-blue dark:bg-blue-600 text-white" data-id="element-8324">
                        <div className="ml-1" data-id="element-8325">▶</div>
                      </button>
                      <div className="flex-grow h-10 flex items-center" data-id="element-8326">
                        {post.audioWaveform.map((level, i) => <div key={i} className="w-1 mx-0.5 rounded-full bg-gradient-to-t from-blue-500/50 to-indigo-500/50" style={{
                  height: `${level * 32}px`
                }} data-id="element-8327"></div>)}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-of-text-secondary dark:text-gray-400" data-id="element-8328">
                        <span data-id="element-8329">{post.duration}</span>
                      </div>
                    </div>
                    <p className="text-xs text-of-text-secondary dark:text-gray-400 mt-2" data-id="element-8330">
                      🎧 Best experienced with headphones
                    </p>
                  </div>
                </div>}

              {/* Post Actions */}
              <div className="p-3" data-id="element-8331">
                <div className="flex items-center justify-between" data-id="element-8332">
                  <div className="flex items-center gap-3" data-id="element-8333">
                    <button className="flex items-center gap-1 group" onClick={() => toggleLike(post.id)} data-id="element-8334">
                      <Heart size={18} className={likedPosts[post.id] ? 'text-blue-500' : 'text-of-text-secondary dark:text-gray-400 group-hover:text-blue-400'} fill={likedPosts[post.id] ? 'currentColor' : 'none'} data-id="element-8335" />
                      <span className="text-xs dark:text-gray-400" data-id="element-8336">
                        {likedPosts[post.id] ? post.likes + 1 : post.likes}
                      </span>
                    </button>
                    <button className="flex items-center gap-1" data-id="element-8337">
                      <MessageCircle size={18} className="text-of-text-secondary dark:text-gray-400" data-id="element-8338" />
                      <span className="text-xs dark:text-gray-400" data-id="element-8339">
                        {post.comments}
                      </span>
                    </button>
                    <button className="flex items-center gap-1" data-id="element-8340">
                      <Share2 size={18} className="text-of-text-secondary dark:text-gray-400" data-id="element-8341" />
                      <span className="text-xs dark:text-gray-400" data-id="element-8342">Share</span>
                    </button>
                  </div>
                  <button className="flex items-center gap-1" data-id="element-8343">
                    <Gift size={18} className="text-blue-500 dark:text-blue-400" data-id="element-8344" />
                    <span className="text-xs dark:text-gray-400" data-id="element-8345">Gift</span>
                  </button>
                </div>
                <div className="flex items-center gap-1 mt-2" data-id="element-8346">
                  <div className="flex -space-x-2" data-id="element-8347">
                    <div className="w-5 h-5 rounded-full border border-white dark:border-gray-800 bg-of-gray dark:bg-gray-600" data-id="element-8348"></div>
                    <div className="w-5 h-5 rounded-full border border-white dark:border-gray-800 bg-of-gray dark:bg-gray-600" data-id="element-8349"></div>
                  </div>
                  <span className="text-xs text-of-text-secondary dark:text-gray-400" data-id="element-8350">
                    Liked by Michael and {(post.likes - 1).toLocaleString()}{' '}
                    others
                  </span>
                </div>
                <div className="text-xs text-of-text-secondary dark:text-gray-400 mt-1" data-id="element-8351">
                  View all {post.comments} comments
                </div>
              </div>
            </div>) : <div className="bg-white dark:bg-gray-800 rounded-xl border border-of-gray dark:border-gray-700 p-8 text-center" data-id="element-8352">
            <div className="w-16 h-16 mx-auto flex items-center justify-center bg-of-gray dark:bg-gray-700 rounded-full mb-4" data-id="element-8353">
              {activeTab === 'media' ? <ImageIcon size={24} className="text-of-text-secondary dark:text-gray-500" data-id="element-8354" /> : activeTab === 'voice' ? <Mic size={24} className="text-of-text-secondary dark:text-gray-500" data-id="element-8355" /> : <Heart size={24} className="text-of-text-secondary dark:text-gray-500" data-id="element-8356" />}
            </div>
            <h3 className="text-lg font-bold mb-2 dark:text-white" data-id="element-8357">
              No {activeTab} content yet
            </h3>
            <p className="text-of-text-secondary dark:text-gray-400 max-w-md mx-auto" data-id="element-8358">
              Harper hasn't posted any {activeTab} content yet. Check back soon!
            </p>
          </div>}
      </div>

      {/* Mission Card */}
      <div className="bg-blue-50 dark:bg-blue-900/30 rounded-xl border border-blue-100 dark:border-blue-800/50 p-4" data-id="element-8359">
        <div className="flex items-center gap-3" data-id="element-8360">
          <div className="w-10 h-10 flex items-center justify-center bg-blue-100 dark:bg-blue-800/30 rounded-full text-blue-500 dark:text-blue-400" data-id="element-8361">
            🔥
          </div>
          <div data-id="element-8362">
            <h3 className="font-medium dark:text-white" data-id="element-8363">Daily Mission</h3>
            <p className="text-sm text-of-text-primary dark:text-gray-300" data-id="element-8364">
              Join Harper's yoga session
            </p>
          </div>
        </div>
        <div className="mt-3" data-id="element-8365">
          <div className="flex items-center justify-between mb-1" data-id="element-8366">
            <span className="text-xs text-of-text-secondary dark:text-gray-400" data-id="element-8367">
              Progress
            </span>
            <span className="text-xs text-of-text-secondary dark:text-gray-400" data-id="element-8368">
              {Object.values(likedPosts).filter(Boolean).length}/3
            </span>
          </div>
          <div className="h-2 bg-of-gray dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-8369">
            <div className="h-full bg-of-blue dark:bg-blue-600 rounded-full" style={{
            width: `${Math.min(Object.values(likedPosts).filter(Boolean).length / 3 * 100, 100)}%`
          }} data-id="element-8370"></div>
          </div>
        </div>
        <div className="flex items-center justify-between mt-3" data-id="element-8371">
          <div className="flex items-center gap-1" data-id="element-8372">
            <span className="text-blue-500 dark:text-blue-400" data-id="element-8373">💙</span>
            <span className="text-sm dark:text-gray-300" data-id="element-8374">+20</span>
          </div>
          <button className="text-xs px-3 py-1.5 bg-of-blue dark:bg-blue-600 rounded-full text-white font-medium" data-id="element-8375">
            Join Session
          </button>
        </div>
      </div>

      {/* Unlock Modal */}
      {showUnlockModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-8376">
          <div className="w-full max-w-sm bg-white dark:bg-gray-800 rounded-xl border border-of-gray dark:border-gray-700 overflow-hidden animate-in zoom-in-90 duration-300" data-id="element-8377">
            <div className="p-4 border-b border-of-gray dark:border-gray-700" data-id="element-8378">
              <h3 className="font-bold dark:text-white" data-id="element-8379">
                Unlock Premium Content
              </h3>
            </div>
            <div className="p-4" data-id="element-8380">
              <p className="text-sm text-of-text-secondary dark:text-gray-300 mb-4" data-id="element-8381">
                Unlock this exclusive content from Harper for 8 gems. This is a
                one-time purchase.
              </p>
              <div className="flex items-center justify-center gap-2 mb-4" data-id="element-8382">
                <div className="w-16 h-16 rounded-full bg-of-blue dark:bg-blue-600 flex items-center justify-center text-2xl" data-id="element-8383">
                  💎
                </div>
              </div>
              <div className="flex items-center justify-between mb-4" data-id="element-8384">
                <span className="text-sm dark:text-gray-300" data-id="element-8385">
                  Your balance:
                </span>
                <span className="font-bold dark:text-white" data-id="element-8386">15 💎</span>
              </div>
              <div className="flex items-center justify-between mb-4" data-id="element-8387">
                <span className="text-sm dark:text-gray-300" data-id="element-8388">Cost:</span>
                <span className="font-bold text-blue-500 dark:text-blue-400" data-id="element-8389">
                  -8 💎
                </span>
              </div>
              <div className="flex gap-2" data-id="element-8390">
                <button className="flex-1 py-2 bg-of-gray dark:bg-gray-700 rounded-lg text-sm dark:text-gray-300" onClick={() => setShowUnlockModal(false)} data-id="element-8391">
                  Cancel
                </button>
                <button className="flex-1 py-2 bg-of-blue dark:bg-blue-600 rounded-lg text-white text-sm font-medium" onClick={confirmUnlock} data-id="element-8392">
                  Unlock Now
                </button>
              </div>
            </div>
          </div>
        </div>}

      <style jsx data-id="element-8393">{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        @keyframes float-up {
          0% {
            opacity: 0;
            transform: translateY(0);
          }
          20% {
            opacity: 1;
          }
          100% {
            opacity: 0;
            transform: translateY(-20px);
          }
        }
        .animate-float-up {
          animation: float-up 1.5s ease-out forwards;
        }
        .shadow-glow {
          box-shadow: 0 0 15px;
        }
      `}</style>
    </section>;
};